import {cloneDeep, findIndex, isArray} from 'lodash';
import React from 'react';
import {edit} from 'react-edit';
import * as sort from 'sortabular';

export function buildColumns(columnsDefs) {
  const getSortingColumns = () => this.state.sortingColumns || {};

  const header = (sortable) => {
    return (value, column) => {
      const sortableProps = sortable(value, column);

      let sortIcon = (<i className={'fa fa-sort'} />);
      if (sortableProps.className === 'sort sort-desc') {
        sortIcon = (<i className={'fa fa-caret-down'} />);
      } else if (sortableProps.className === 'sort sort-asc') {
        sortIcon = (<i className={'fa fa-caret-up'} />);
      }

      return (
        <div className={'sort-container'}>
          {sortable &&
              <span {...sortableProps}>
                {value}
                {sortIcon}
              </span>
          }
        </div>
      );
    };
  };

  const sortable = sort.sort({
    getSortingColumns,
    onSort: (selectedColumn) => {
      if (this.props.sortHandler) {
        this.props.sortHandler(selectedColumn);
      } else {
        this.setState({
          sortingColumns: sort.byColumn({
            sortingColumns: this.state.sortingColumns,
            selectedColumn,
          }),
        });
      }
    },
    strategy: sort.strategies.byProperty,
  });

  const tableColumns = mapTableColumns(columnsDefs, sortable, header, this.props);

  this.setState({columns: tableColumns});
}

/**
 * Returns the array of columnDefs config remapped to optionally include sortable event handlers
 * @param columnsDefs - object, user-defined datatable column configs
 * @param sortable - function, returns a function that generates React props
 * @param header - function, returns a function that generates a table heading
 * @param props - object, props passed to the DataTable component which is being rendered
 * @returns {*}
 */
export function mapTableColumns(columnsDefs, sortable, header, props) {
  return cloneDeep(columnsDefs).map((col) => {
    if (col.sortable) {
      col.header['transforms'] = isArray(col.header['transforms'])
        ? col.header['transforms'].concat([sortable])
        : [sortable];

      col.header['formatters'] = isArray(col.header['formatters'])
        ? col.header['formatters'].concat([header(sortable)])
        : [header(sortable)];
    }

    return col;
  });
}

export function paginate({page, perPage, totalPages, totalCount = 0}) {
  return (rows = []) => {
    const amountOfPages = totalPages ? totalPages : Math.ceil(rows.length / perPage);
    const sliceStartPage = (page > 1) ? (page - 1) : 0;
    const count = totalCount ? totalCount : rows.length;
    const sliceStart = sliceStartPage * perPage;
    const sliceEnd = (sliceStartPage * perPage) + perPage;
    rows = totalPages ? rows : rows.slice(sliceStart, sliceEnd);

    if (totalCount && rows.length > perPage) {
      throw Error(`Row count (${rows.length}) should not be greater than perPage (${perPage}) value.
        Please pass an array with <= ${perPage} rows, or update the 'perPage' prop.`);
    }

    return {
      amount: amountOfPages,
      rows,
      count,
      sliceStart: sliceStart + 1,
      sliceEnd: sliceStart + rows.length,
    };
  };
}

/**
 * Returns all IDs that have property set in recursive array
 * @param arr - object, user-defined datatable column configs
 * @param arrayKey - key that recursively could contain same elements
 * @param propertyKey - key to check for populating return
 * @returns array with all IDs that had the property key set truthy
 */
export const recurseAllObjectsByKey = (arr, arrayKey, propertyKey) => {
  let result = [];

  arr.forEach((item) => {
    if (item[propertyKey]) {
      result.push(item.id ? item.id : item.accessorKey);
    }

    if (Array.isArray(item[arrayKey])) {
      result.push(...recurseAllObjectsByKey(item[arrayKey], arrayKey, propertyKey));
    }
  });

  return result;
};
